import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  Divider,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Info,
  Dashboard,
  AccountCircle,
  Logout,
  Login,
  MonetizationOn,
  Insights as InsightsIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { useI18n } from '../contexts/I18nContext';
import { useUserStore } from '../stores/userStore';

interface HeaderProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
}

const Header: React.FC<HeaderProps> = ({ currentLanguage, onLanguageChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isAuthenticated, logout } = useUserStore();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const navItems = [
    { label: t('navigation.pricing'), path: '/pricing', icon: <MonetizationOn /> },
    { label: t('navigation.insights'), path: '/insights', icon: <InsightsIcon /> },
    { label: t('navigation.about'), path: '/about', icon: <Info /> },
  ];

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    handleMobileMenuClose();
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const renderNavLinks = () =>
    navItems.map((item) => (
      <Button
        key={item.path}
        component={RouterLink}
        to={item.path}
        color="inherit"
        startIcon={item.icon}
        sx={{
          backgroundColor:
            location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {item.label}
      </Button>
    ));

  const renderMobileMenu = () => (
    <Menu
      anchorEl={mobileMenuAnchorEl}
      open={Boolean(mobileMenuAnchorEl)}
      onClose={handleMobileMenuClose}
    >
      {navItems.map((item) => (
        <MenuItem key={item.path} onClick={() => handleNavigate(item.path)}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          {item.label}
        </MenuItem>
      ))}
      <Divider />
      {isAuthenticated && (
        <MenuItem onClick={() => handleNavigate('/dashboard')}>
          <ListItemIcon>
            <Dashboard />
          </ListItemIcon>
          {t('navigation.dashboard')}
        </MenuItem>
      )}
    </Menu>
  );

  const renderUserMenu = () => (
    <Menu
      anchorEl={userMenuAnchorEl}
      open={Boolean(userMenuAnchorEl)}
      onClose={handleUserMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          navigate('/user');
        }}
      >
        <ListItemIcon>
          <AccountCircle />
        </ListItemIcon>
        Profile
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          navigate('/dashboard');
        }}
      >
        <ListItemIcon>
          <Dashboard />
        </ListItemIcon>
        {t('navigation.dashboard')}
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        {t('header.logout')}
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar position="static" color="primary" elevation={2}>
      <Toolbar>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{ flexGrow: 1, fontWeight: 'bold', textDecoration: 'none', color: 'inherit' }}
        >
          Conformity Core
        </Typography>

        {isMobile ? (
          <>
            <LanguageSwitcher
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
            />
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              aria-label={t('header.mobileMenu')}
            >
              <MenuIcon />
            </IconButton>
            {renderMobileMenu()}
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {renderNavLinks()}
            <Button
              variant="contained"
              color="secondary"
              component={RouterLink}
              to="/demo"
              sx={{ ml: 1 }}
            >
              Request a Demo
            </Button>
            <LanguageSwitcher
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
            />

            {isAuthenticated ? (
              <>
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  color="inherit"
                  startIcon={<Dashboard />}
                  sx={{
                    backgroundColor:
                      location.pathname === '/dashboard'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('navigation.dashboard')}
                </Button>
                <Tooltip title={t('header.userMenu')}>
                  <IconButton
                    color="inherit"
                    onClick={handleUserMenuOpen}
                    sx={{ ml: 1 }}
                    aria-label={t('header.userMenu')}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                      {user?.name?.charAt(0)?.toUpperCase() || <AccountCircle />}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                {renderUserMenu()}
              </>
            ) : (
              <Button
                color="inherit"
                startIcon={<Login />}
                onClick={() => navigate('/auth')}
                sx={{ ml: 1 }}
              >
                {t('header.login')}
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
